import React from 'react';
import { i18n } from '@international/mastodon-i18n';
import useTranslatedArray from 'hooks/useTranslatedArray';
import useTranslatedRecord from 'hooks/useTranslatedRecord';
import { readString } from 'hooks/lib/translationGuards';
import { KeyValuePair } from 'types/mastodon';
import { I18nRenderedTranslation } from 'types/i18n';
import DOMPurify from 'isomorphic-dompurify';
import IconTextList from '../IconTextList';

interface HelpfulToKnowProps {
  translationKey: string;
  noTitle?: boolean;
  className?: string;
}

interface HelpfulItem {
  icon: string;
  text: string;
}

const HelpfulToKnow: React.FC<I18nRenderedTranslation<HelpfulToKnowProps>> = ({
  translationKey,
  t,
  noTitle = false,
  className = '',
}) => {
  // The defensive guard against malformed translation values (ESCR-1618:
  // stringified arrays in non-English locales) lives in the hook so every
  // consumer gets it for free.
  const rawItems = useTranslatedArray<HelpfulItem>(translationKey, t, {
    subPath: 'items',
  });
  // Resolve the translation root once via the shared `useTranslatedRecord`
  // helper instead of re-implementing the `t()` + `isPlainObject` guard
  // here; keeps the title lookup honest if the schema ever changes.
  const translationRoot = useTranslatedRecord(translationKey, t);

  const items = React.useMemo<KeyValuePair<string, React.ReactNode>[]>(
    () =>
      rawItems.map(({ icon, text }) => ({
        itemKey: icon?.replace(/{|}/g, ''),
        itemValue: (
          <span
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }}
          />
        ),
      })),
    [rawItems],
  );

  const title = React.useMemo(() => {
    if (noTitle) return '';
    return (
      readString(translationRoot, 'title') ||
      t('components.HelpfulToKnow.title')
    );
  }, [t, translationRoot, noTitle]);

  if (items.length === 0) return null;

  return <IconTextList title={title} items={items} className={className} />;
};

export default i18n.renderTranslation()(
  HelpfulToKnow,
) as React.FC<HelpfulToKnowProps>;
